
import {take, combineLatest} from 'rxjs/operators';
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormSaveType, FormServiceParams, FormV2Service } from '../../../shared/services/form-v2.service';
import { Observer ,  BehaviorSubject } from 'rxjs';
import { JsonApiSingeModelResponse } from '../../../shared/services/app/web-socket/http-response';
import { HandleResponseAction } from '../../../ngrx/actions/root.action';
import { SUBSCRIPTION_PATH } from '@atlaz/billing/constants/billing-subscription';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../../ngrx/state';
import { RouterNavigateService } from '../../../shared/services/router-navigate.service';
import { environment } from '../../../../environments/environment';
import { BillingPublicApiService } from '@atlaz/billing/shared/billing-public-api.service';
import { BillingSubscriptionEntity } from '@atlaz/billing/interfaces/billing-subscription';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'change-card-form',
  templateUrl: './change-card-form.component.html',
  styleUrls: ['./change-card-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormV2Service]
})
export class ChangeCardFormComponent implements OnInit, AfterViewInit {
  @Input() billingSub: BillingSubscriptionEntity;

  formObserver: Observer<any> = {
    next: (resp: JsonApiSingeModelResponse<any>) => {
      this._store.dispatch(new HandleResponseAction(resp));
      if (resp.meta && resp.meta.there_d_secure_required) {
        this._routerNav.navigate3DSecurePopup();
      } else if (resp.meta && resp.meta.attemptToPay) {
        this.showSuccessPaymentPopup();
      } else {
        this.showSuccessCardChange();
      }
    },

    error: error => {
      this.chargeIsRequired ? this.onShowPaymentDeniedPopup() : this.showErrorCardChange();
    },

    complete: () => {}
  };

  formServiceParams: FormServiceParams = {
    saveType: FormSaveType.edit,
    entityToEdit: SUBSCRIPTION_PATH,
    formObserver: this.formObserver,
    prepareFormValue: formValue => {
      return {
        source: formValue['source'],
        id: this.billingSub.id,
        action: 'change-card'
      };
    }
  };

  public lastCardNumbers: string;
  public isPaymentSuccessPopupVisible = false;
  public isPaymentDeniedPopupVisible = false;
  public form: FormGroup;
  public stripeErrors$ = new BehaviorSubject('');
  public stripeLoading$ = new BehaviorSubject(false);
  public loading$ = this.stripeLoading$.pipe(combineLatest(this._formService.isPending$, (a, b) => a || b));
  public showCurrentCardDetails$ = new BehaviorSubject(false);
  public showDeleteCardDetailsConfirmation$ = new BehaviorSubject(false);
  readonly chargeIsRequired$ = this._billingService.chargeIsRequired$;

  public get last4() {
    return this.billingSub.cardData ? this.billingSub.cardData['last4'] : null;
  }

  public get validDate() {
    return this.billingSub.cardData
      ? [this.billingSub.cardData['exp_month'], this.billingSub.cardData['exp_year']].join('/')
      : null;
  }

  private stripe = (<any>window).Stripe(environment.billing.stripePublicKey);
  private cardElement;

  private hasStripeError = false;
  private chargeIsRequired = false;

  constructor(
    private _fb: FormBuilder,
    private _store: Store<AppState>,
    public _formService: FormV2Service,
    private _billingService: BillingPublicApiService,
    private _toastr: ToastrService,
    private _routerNav: RouterNavigateService
  ) {}

  ngOnInit() {
    this.form = this._fb.group({
      source: ['']
    });

    this._formService.initFormParams(this.form, this.formServiceParams);
  }

  ngAfterViewInit() {
    this.createdCardElement();
  }

  private createdCardElement() {
    const el = this.stripe.elements();

    const style = {
      base: {
        color: '#32325d',
        lineHeight: '24px',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    };

    // Create an instance of the card Element
    this.cardElement = el.create('card', {
      style: style,
      hidePostalCode: true
    });

    // Add an instance of the card Element into the `card-element` <div>
    this.cardElement.mount('#card-element');

    // Handle real-time validation errors from the card Element.
    this.cardElement.addEventListener('change', event => {
      if (event.error) {
        this.hasStripeError = true;
        this.stripeErrors$.next(event.error.message);
      } else {
        this.hasStripeError = false;
        this.stripeErrors$.next('');
      }
    });
  }

  showSuccessPaymentPopup() {
    this.isPaymentSuccessPopupVisible = true;
  }
  onShowPaymentDeniedPopup() {
    this.isPaymentDeniedPopupVisible = true;
  }

  showErrorCardChange() {
    this._toastr.error('Payment method has not been changed');
  }

  showSuccessCardChange() {
    this._toastr.success('Payment method has been successfully changed');
    this.onNavigateToBilling();
  }

  onNavigateToBilling() {
    this._routerNav.navigateToBilling();
  }

  onRenewForm() {
    this.isPaymentDeniedPopupVisible = false;
    this.onSubmit();
  }
  onCloseDeniedPopup() {
    this.isPaymentDeniedPopupVisible = false;
  }

  onSubmit() {
    if (this.hasStripeError || this.form.invalid) {
      return false;
    }

    this.chargeIsRequired$.pipe(take(1)).subscribe(chargeIsRequired => (this.chargeIsRequired = chargeIsRequired));

    this.stripeLoading$.next(true);
    this.stripe.createSource(this.cardElement).then(
      result => {
        if (result.error) {
          // Inform the user if there was an error
          this.stripeErrors$.next(result.error.message);
        } else {
          this.lastCardNumbers = result.source.card.last4;
          // Send the source to your server
          this.form.get('source').patchValue(result.source.id);
          this._formService.submit();
        }
        this.stripeLoading$.next(false);
      },
      e => {
        this.stripeLoading$.next(false);
      }
    );

    return false;
  }

  showDetails() {
    this.showCurrentCardDetails$.next(true);
  }

  hideDetails() {
    this.showCurrentCardDetails$.next(false);
  }

  showConfirmDelete() {
    this.showCurrentCardDetails$.next(false);
    this.showDeleteCardDetailsConfirmation$.next(true);
  }

  hideConfirmDelete() {
    this.showDeleteCardDetailsConfirmation$.next(false);
  }
}
